import React, { useEffect } from 'react';

import {
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  Row,
  SortingState,
  Table,
  useReactTable,
} from '@tanstack/react-table';
import { useVirtualizer, VirtualItem, Virtualizer } from '@tanstack/react-virtual';
import { ColDef, computeColumnWidthsGrow } from './calcColumnWidthFast';
import './VirtualizationDataTable.scss';
import { Icon } from '../../icons/Icon';

interface VirtualizedTableProps {
  columns: any[];
  data: any[];
  defaultColumn?: any;
  tableHeight?: number;
  currentFilters?: { columnId: string; filterValue: any }[];
  currentFiltersRef?: React.RefObject<any[]>;
  onFilterChange?: (columnId: string, value: unknown, table?: any) => void;
  searchTerm?: string;
  pinColumn?: string[];
}

const getColumnKey = (hdrOrCol: any) => {
  const headerCol = hdrOrCol?.column ?? hdrOrCol;
  if (headerCol) {
    if (headerCol.columnDef?.accessorKey) return String(headerCol.columnDef.accessorKey);
    if (headerCol.columnDef?.accessor) return String(headerCol.columnDef.accessor);
    if (headerCol.id) return String(headerCol.id);
    if (headerCol.columnDef?.id) return String(headerCol.columnDef.id);

    const h = headerCol.columnDef?.header ?? headerCol.header;
    if (typeof h === 'string' && h) return h;

    if ((typeof h === 'function' || React.isValidElement(h)) && headerCol.id) return String(headerCol.id);
  }

  if (typeof hdrOrCol === 'string') return hdrOrCol;
  return undefined;
};

const matchesPinned = (colKey: string | undefined, pinColumn: string[] = []) => {
  if (!colKey) return false;
  return pinColumn.some((p) => String(p).trim() === String(colKey).trim());
};

const VirtualizationDataTable: React.FC<VirtualizedTableProps> = ({
  columns,
  data,
  defaultColumn,
  tableHeight = '400px',
  currentFilters,
  currentFiltersRef,
  onFilterChange,
  searchTerm,
  pinColumn = [],
}) => {
  const memoColumns = React.useMemo(() => columns ?? [], [columns]);
  const memoData = React.useMemo(() => data ?? [], [data]);
  const memoDefaultColumn = React.useMemo(() => defaultColumn, [defaultColumn]);

  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = React.useState<string>(searchTerm || '');
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [containerWidth, setContainerWidth] = React.useState(0);
  const tableContainerRef = React.useRef<HTMLDivElement>(null);

  const newColumns: ColDef[] = React.useMemo(
    () =>
      memoColumns.map((col: any) => ({
        id: col.id,
        accessorKey: col.accessor,
        header: col.Header ?? col.header,
        widthMode: col.widthMode || 'auto',
        ...col,
      })),
    [memoColumns],
  );

  useEffect(() => {
    if (!tableContainerRef.current) return;
    const obs = new ResizeObserver(() => {
      if (tableContainerRef.current) {
        setContainerWidth(tableContainerRef.current.offsetWidth);
      }
    });

    obs.observe(tableContainerRef.current);

    return () => obs.disconnect();
  }, []);

  // compute widths once
  const computed = React.useMemo(() => {
    return computeColumnWidthsGrow(newColumns, memoData, containerWidth, { sampleSize: 50 });
  }, [newColumns, memoData, containerWidth]);

  useEffect(() => {
    setGlobalFilter(searchTerm ?? '');
  }, [searchTerm, setGlobalFilter]);

  const table = useReactTable({
    data: memoData,
    columns: computed as any,
    defaultColumn: memoDefaultColumn,
    state: {
      columnFilters,
      globalFilter,
      sorting,
    },
    onGlobalFilterChange: setGlobalFilter,
    onColumnFiltersChange: setColumnFilters,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableColumnPinning: true,
  });

  const headerGroups = table.getHeaderGroups();
  const { resolvedPinnedColumns, pinnedLeftMap, rightMostPinnedKey } = React.useMemo(() => {
    const resolved: string[] = [];
    const leftMap: Record<string, number> = {};

    if (!headerGroups || headerGroups.length === 0) {
      return { resolvedPinnedColumns: [], pinnedLeftMap: {}, rightMostPinnedKey: undefined as string | undefined };
    }

    const headers = headerGroups[headerGroups.length - 1].headers;

    let maxIndex = -1;
    for (const p of pinColumn) {
      const idx = headers.findIndex((h) => getColumnKey(h) === p);
      if (idx > maxIndex) maxIndex = idx;
    }

    if (maxIndex === -1) {
      for (let i = 0; i < headers.length; i++) {
        const key = getColumnKey(headers[i]);
        if (key && pinColumn.some((p) => String(p).trim() === String(key).trim())) {
          resolved.push(key);
        }
      }
    } else {
      for (let i = 0; i <= maxIndex; i++) {
        const key = getColumnKey(headers[i]);
        if (key) resolved.push(key);
      }
    }

    let leftAcc = 0;
    for (const header of headers) {
      const key = getColumnKey(header);
      if (key && resolved.includes(key)) {
        leftMap[key] = leftAcc;
        leftAcc += header.getSize();
      }
    }

    const rightMost = resolved.length > 0 ? resolved[resolved.length - 1] : undefined;

    return { resolvedPinnedColumns: resolved, pinnedLeftMap: leftMap, rightMostPinnedKey: rightMost };
  }, [pinColumn, headerGroups]);

  return (
    <div className='custom-virtualization-table-wrapper'>
      <div
        className='custom-virtualization-table-container'
        ref={tableContainerRef}
        style={{
          height: tableHeight,
        }}
      >
        <table
          className='custom-virtualization-table'
          style={{
            borderCollapse: 'separate',
            borderSpacing: 0,
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          <thead className='custom-virtualization-table-header'>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const sort = header.column.getIsSorted();
                  const FilterComponent = header.column.columnDef.meta?.Filter;
                  const colKey = getColumnKey(header);
                  const pinned = matchesPinned(colKey, resolvedPinnedColumns);
                  const leftPx = pinned ? (pinnedLeftMap[colKey as string] ?? 0) : undefined;

                  return (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      className={pinned ? 'pinned-column' : undefined}
                      style={{
                        width: header.getSize(),
                        height: '40px',
                        alignItems: 'center',
                        borderRight: pinned && colKey === rightMostPinnedKey ? '1px solid var(--border-color)' : 'none',

                        ...(pinned
                          ? {
                            position: 'sticky',
                            left: leftPx,
                            zIndex: 20,
                          }
                          : {}),
                      }}
                    >
                      <div
                        {...{
                          className: header.column.getCanSort() ? 'cursor-pointer select-none' : '',
                          onClick: header.column.getToggleSortingHandler(),
                        }}
                      >
                        <div className='header-content'>
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {sort === 'asc' && <Icon type='arrow-up' size={12} color={'#F7F9F9'} />}
                          {sort === 'desc' && <Icon type='arrow-down' size={12} color={'#F7F9F9'} />}
                          {header.column.getCanFilter() && FilterComponent && (
                            <FilterComponent
                              column={header.column}
                              table={table}
                              appliedFilters={currentFilters}
                              currentFiltersRef={header.column.columnDef.meta?.currentFiltersRef ?? currentFiltersRef}
                              onFilterChange={(colId: string, val: unknown) => onFilterChange?.(colId, val, table)}
                            />
                          )}
                        </div>
                      </div>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>

          <TableBody
            table={table}
            tableContainerRef={tableContainerRef}
            pinColumn={resolvedPinnedColumns}
            pinnedLeftMap={pinnedLeftMap}
            rightMostPinnedKey={rightMostPinnedKey}
          />
        </table>
      </div>
    </div>
  );
};

interface TableBodyProps {
  table: Table<any>;
  tableContainerRef: React.RefObject<HTMLDivElement>;
  pinColumn?: string[];
  pinnedLeftMap?: Record<string, number>;
  rightMostPinnedKey?: string;
}

function TableBody({
  table,
  tableContainerRef,
  pinColumn = [],
  pinnedLeftMap = {},
  rightMostPinnedKey,
}: TableBodyProps) {
  const { rows } = table.getRowModel();

  const rowVirtualizer = useVirtualizer<HTMLDivElement, HTMLTableRowElement>({
    count: rows.length,
    estimateSize: () => 44,
    getScrollElement: () => tableContainerRef.current,
    overscan: 5,
    measureElement: undefined,
  });

  return (
    <tbody
      className='custom-virtualization-table-body'
      style={{
        height: `${rowVirtualizer.getTotalSize()}px`,
        position: 'relative',
      }}
    >
      {rowVirtualizer.getVirtualItems().map((virtualRow) => {
        const row = rows[virtualRow.index];
        return (
          <TableBodyRow
            key={row.id}
            row={row}
            virtualRow={virtualRow}
            rowVirtualizer={rowVirtualizer}
            pinColumn={pinColumn}
            pinnedLeftMap={pinnedLeftMap}
            rightMostPinnedKey={rightMostPinnedKey}
          />
        );
      })}
    </tbody>
  );
}

interface TableBodyRowProps {
  row: Row<any>;
  virtualRow: VirtualItem;
  rowVirtualizer?: Virtualizer<HTMLDivElement, HTMLTableRowElement>;
  pinColumn?: string[];
  pinnedLeftMap?: Record<string, number>;
  rightMostPinnedKey?: string;
}

function TableBodyRow({ row, virtualRow, pinColumn = [], pinnedLeftMap = {}, rightMostPinnedKey }: TableBodyRowProps) {
  return (
    <tr
      data-index={virtualRow.index}
      key={row.id}
      style={{
        height: `${virtualRow.size}px`,
        transform: `translateY(${virtualRow.start}px)`,
      }}
    >
      {row.getVisibleCells().map((cell: any) => {
        const value = cell.getValue?.();
        const isString = typeof value === 'string' ? value.trim() : value;
        const isNumeric =
          cell.column.id.toLowerCase() !== 'well' &&
          isString !== undefined &&
          isString !== null &&
          !isNaN(Number(isString));

        const colKey = getColumnKey(cell.column);
        const isPinned = matchesPinned(colKey, pinColumn);
        const leftPx = isPinned ? (pinnedLeftMap[colKey as string] ?? 0) : undefined;

        return (
          <td
            key={cell.id}
            className={`${cell.column.getIsPinned() ? 'pin-column' : ''} ${isPinned ? 'pinned-column' : ''}`}
            style={{
              width: cell.column.getSize(),
              height: `${virtualRow.size}px`,
              justifyContent: 'center',
              flexDirection: 'column',
              borderRight: isPinned && colKey === rightMostPinnedKey ? '1px solid var(--border-color)' : 'none',
              paddingRight: isPinned ? (isNumeric ? '19px' : '0px') : isString ? (isNumeric ? '19px' : '0px') : '0px',
              alignItems: isPinned
                ? isNumeric
                  ? 'flex-end'
                  : 'flex-start'
                : isString
                  ? isNumeric
                    ? 'flex-end'
                    : 'flex-start'
                  : 'flex-start',
              ...(isPinned
                ? {
                  position: 'sticky',
                  left: leftPx,
                  zIndex: 20,
                }
                : {}),
            }}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </td>
        );
      })}
    </tr>
  );
}

export default VirtualizationDataTable;
